import { useForm } from '@tanstack/react-form';
import { Bot, Braces, Tag, UserRound, Variable, type LucideIcon } from 'lucide-react';
import { Button } from '@sara/ui/components/button';
import { Input } from '@sara/ui/components/input';
import { Textarea } from '@sara/ui/components/textarea';
import { Field, FieldContent, FieldError, FieldLabel } from '@sara/ui/components/field';
import { TemplateVariablesField } from './TemplateVariablesField';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@sara/ui/components/select';
import { SHOW_ORG_LOGO_LABELS } from '../../lib/show-org-logo';
import { CandidateConsentFields } from '../CandidateConsentFields';
import {
  createInterviewTypeVersionFormSchema,
  type CreateInterviewTypeVersionFormValues,
} from './schemas';

export interface SelectorOption {
  id: string;
  name: string;
  latestVersionId: string;
  latestVersion: number;
}

/** An extra item to add to a selector when the default pinned version is not the latest. */
export interface ExtraSelectItem {
  versionId: string;
  label: string;
}

interface CreateInterviewTypeVersionFormProps {
  templateName: string;
  templateDescription: string;
  elevenlabsAgentId: string | null;
  defaultValues: CreateInterviewTypeVersionFormValues;
  /**
   * Whether the org has an uploaded logo. When false, the candidate logo
   * select shows a hint prompting the admin to upload a logo in Branding first.
   */
  orgHasLogo: boolean;
  extractions?: SelectorOption[];
  rubrics?: SelectorOption[];
  /**
   * Extra item to include in the extraction selector when the default is an older pinned version.
   * Labeled "{name} · v{version} (current)".
   */
  extraExtractionItem?: ExtraSelectItem;
  /**
   * Extra item to include in the rubric selector when the default is an older pinned version.
   */
  extraRubricItem?: ExtraSelectItem;
  onSubmit: (values: CreateInterviewTypeVersionFormValues) => void;
  onCancel: () => void;
  isPending: boolean;
}

const subLabelClassName = 'text-[12px] font-medium text-foreground/80';
const fieldHelpClassName = '-mt-1 text-[12.5px] leading-snug text-muted-foreground';
const textareaClassName =
  'flex w-full rounded-md border border-border/70 bg-background px-3 py-2.5 font-mono text-[12.5px] leading-relaxed text-foreground/90 ring-offset-background placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

function SectionCard({
  icon: Icon,
  title,
  hint,
  trailing,
  children,
}: {
  icon: LucideIcon;
  title: string;
  hint?: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-border/70 bg-card">
      <header className="flex items-start justify-between gap-3 border-b border-border/60 bg-muted/30 px-4 py-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border/70 bg-background text-foreground/70">
            <Icon className="h-4 w-4" strokeWidth={1.75} />
          </span>
          <div className="flex flex-col min-w-0 pt-0.5">
            <h3 className="text-[14px] font-semibold leading-tight text-foreground">{title}</h3>
            {hint && <p className="text-[12.5px] leading-snug text-muted-foreground">{hint}</p>}
          </div>
        </div>
        {trailing && <div className="shrink-0">{trailing}</div>}
      </header>
      <div className="p-4 sm:p-5 space-y-5">{children}</div>
    </section>
  );
}

export function CreateInterviewTypeVersionForm({
  templateName,
  templateDescription,
  elevenlabsAgentId,
  defaultValues,
  orgHasLogo,
  extractions = [],
  rubrics = [],
  extraExtractionItem,
  extraRubricItem,
  onSubmit,
  onCancel,
  isPending,
}: CreateInterviewTypeVersionFormProps) {
  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: createInterviewTypeVersionFormSchema,
    },
    onSubmit: ({ value }) => {
      onSubmit(value);
    },
  });

  // The prompt is prefilled from the template's live agent config. When that read
  // was unavailable (`promptLoaded` false) the wipe-guard drops the prompt on
  // save, so the editors are disabled (with a note) to avoid silently discarding
  // typed input — mirrors the edit modal. Static for this page's lifetime.
  const promptUnavailable = !defaultValues.promptLoaded;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      autoComplete="off"
      className="space-y-5"
    >
      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
        {/* LEFT — the agent's prompt (pass-through, live). Prominent + sticky so
            it stays in view while the right column's config scrolls. */}
        <div className="space-y-5 lg:sticky lg:top-6">
          <SectionCard
            icon={Bot}
            title="Agent prompt"
            hint="Saved live to this template's ElevenLabs agent."
            trailing={
              elevenlabsAgentId ? (
                <code
                  className="font-mono text-[11.5px] text-muted-foreground"
                  data-testid="version-agent-ref"
                  title={elevenlabsAgentId}
                >
                  {elevenlabsAgentId}
                </code>
              ) : undefined
            }
          >
            {promptUnavailable && (
              <p className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-[12.5px] leading-snug text-muted-foreground">
                Couldn&rsquo;t load the agent&rsquo;s live prompt right now. Reopen this page to
                edit it once ElevenLabs is reachable — the version&rsquo;s other fields still save.
              </p>
            )}

            <form.Field name="systemPrompt">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                    System prompt
                  </FieldLabel>
                  <FieldContent>
                    <Textarea
                      id={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={isPending || promptUnavailable}
                      className="min-h-[52vh] resize-y font-mono text-[12.5px] leading-relaxed"
                      autoComplete="off"
                      data-testid="version-system-prompt"
                    />
                  </FieldContent>
                </Field>
              )}
            </form.Field>

            <form.Field name="firstMessage">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                    First message
                  </FieldLabel>
                  <FieldContent>
                    <Input
                      id={field.name}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={isPending || promptUnavailable}
                      autoComplete="off"
                      placeholder="Hi {{first_name}}! Thanks for taking the time today…"
                      className="h-9"
                      data-testid="version-first-message"
                    />
                  </FieldContent>
                </Field>
              )}
            </form.Field>
          </SectionCard>
        </div>

        {/* RIGHT — the versioned config: what the agent collects and how the
            candidate experiences the call. */}
        <div className="space-y-5">
          {/* Variables — pre-call inputs the agent's prompt references as {{key}}.
              Placed before Analysis: inputs precede the call, Analysis follows it. */}
          <SectionCard
            icon={Variable}
            title="Dynamic variables"
            hint="Inputs the agent's prompt references as {{key}}. Captured when a session is created and passed to the agent at the start of the call."
          >
            <form.Field name="dynamicVariableDefinitions">
              {(field) => (
                <TemplateVariablesField
                  value={field.state.value}
                  onChange={(next) => field.handleChange(next)}
                  disabled={isPending}
                />
              )}
            </form.Field>
          </SectionCard>

          {/* Analysis — optional post-call extraction and scoring. */}
          <SectionCard
            icon={Braces}
            title="Analysis"
            hint="Optional post-call extraction and scoring to run on each conversation."
          >
            <form.Field name="extractionVersionId">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                    Extraction (optional)
                  </FieldLabel>
                  <p className={fieldHelpClassName}>
                    Runs after the call to extract structured data from the transcript.
                  </p>
                  <FieldContent>
                    <Select
                      value={field.state.value ?? 'none'}
                      onValueChange={(v) => field.handleChange(v)}
                      disabled={isPending}
                    >
                      <SelectTrigger id={field.name} className="h-9">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {extraExtractionItem && (
                          <SelectItem value={extraExtractionItem.versionId}>
                            {extraExtractionItem.label}
                          </SelectItem>
                        )}
                        {extractions.map((e) => (
                          <SelectItem key={e.latestVersionId} value={e.latestVersionId}>
                            {e.name} · v{e.latestVersion}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldContent>
                </Field>
              )}
            </form.Field>

            <form.Field name="rubricVersionId">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                    Rubric (optional)
                  </FieldLabel>
                  <p className={fieldHelpClassName}>
                    Scores each conversation against a rubric after the call completes.
                  </p>
                  <FieldContent>
                    <Select
                      value={field.state.value ?? 'none'}
                      onValueChange={(v) => field.handleChange(v)}
                      disabled={isPending}
                    >
                      <SelectTrigger id={field.name} className="h-9">
                        <SelectValue placeholder="None" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {extraRubricItem && (
                          <SelectItem value={extraRubricItem.versionId}>
                            {extraRubricItem.label}
                          </SelectItem>
                        )}
                        {rubrics.map((r) => (
                          <SelectItem key={r.latestVersionId} value={r.latestVersionId}>
                            {r.name} · v{r.latestVersion}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldContent>
                </Field>
              )}
            </form.Field>
          </SectionCard>

          <SectionCard
            icon={UserRound}
            title="Candidate experience"
            hint="What the candidate sees before, during, and after the conversation."
          >
            <form.Field name="candidateTitle">
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <Field data-invalid={hasError || undefined}>
                    <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                      Call title <span className="text-destructive">*</span>
                    </FieldLabel>
                    <FieldContent>
                      <Input
                        id={field.name}
                        placeholder="e.g. Bartender screening call"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        disabled={isPending}
                        aria-invalid={hasError}
                        autoComplete="off"
                        className="h-9"
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </FieldContent>
                  </Field>
                );
              }}
            </form.Field>

            <form.Field name="candidateDescription">
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <Field data-invalid={hasError || undefined}>
                    <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                      Lobby description <span className="text-destructive">*</span>
                    </FieldLabel>
                    <FieldContent>
                      <textarea
                        id={field.name}
                        placeholder="What the candidate sees while waiting to start…"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        disabled={isPending}
                        aria-invalid={hasError}
                        rows={4}
                        className={textareaClassName}
                        autoComplete="off"
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </FieldContent>
                  </Field>
                );
              }}
            </form.Field>

            <form.Field name="candidateEndOfCallNotes">
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <Field data-invalid={hasError || undefined}>
                    <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                      End-of-call notes <span className="text-destructive">*</span>
                    </FieldLabel>
                    <FieldContent>
                      <textarea
                        id={field.name}
                        placeholder="What the candidate sees once the conversation ends…"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        disabled={isPending}
                        aria-invalid={hasError}
                        rows={4}
                        className={textareaClassName}
                        autoComplete="off"
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </FieldContent>
                  </Field>
                );
              }}
            </form.Field>

            <form.Field name="candidateDeclinedMessage">
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <Field data-invalid={hasError || undefined}>
                    <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                      Declined message <span className="text-destructive">*</span>
                    </FieldLabel>
                    <p className={fieldHelpClassName}>
                      Shown on the &ldquo;Got it&rdquo; screen when a candidate declines. Supports{' '}
                      <code>**bold**</code>; leave a blank line to start a new paragraph.
                    </p>
                    <FieldContent>
                      <textarea
                        id={field.name}
                        placeholder="What the candidate sees after declining the conversation…"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        disabled={isPending}
                        aria-invalid={hasError}
                        rows={4}
                        className={textareaClassName}
                        autoComplete="off"
                      />
                      <FieldError errors={field.state.meta.errors} />
                    </FieldContent>
                  </Field>
                );
              }}
            </form.Field>

            <form.Field name="candidateShowOrgLogo">
              {(field) => (
                <Field>
                  <FieldLabel htmlFor={field.name} className={subLabelClassName}>
                    Organization logo
                  </FieldLabel>
                  <FieldContent>
                    <Select
                      value={field.state.value}
                      onValueChange={(v) => field.handleChange(v as typeof field.state.value)}
                      disabled={isPending}
                    >
                      <SelectTrigger id={field.name} className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="default">{SHOW_ORG_LOGO_LABELS.default}</SelectItem>
                        <SelectItem value="show">{SHOW_ORG_LOGO_LABELS.show}</SelectItem>
                        <SelectItem value="hide">{SHOW_ORG_LOGO_LABELS.hide}</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className={fieldHelpClassName}>
                      {orgHasLogo
                        ? 'Org default is managed in Settings → Branding.'
                        : 'Upload an organization logo in Settings → Branding for the logo to appear.'}
                    </p>
                  </FieldContent>
                </Field>
              )}
            </form.Field>

            <form.Field name="consentConfig">
              {(field) => (
                <Field>
                  <FieldLabel className={subLabelClassName}>
                    Consent &amp; secondary link
                  </FieldLabel>
                  <FieldContent>
                    <CandidateConsentFields
                      value={field.state.value}
                      onChange={(next) => field.handleChange(next)}
                      disabled={isPending}
                    />
                  </FieldContent>
                </Field>
              )}
            </form.Field>
          </SectionCard>

          {/* Details — read-only metadata. Edited in the "Edit interview type"
              dialog (name / slug / description); not versioned. */}
          <SectionCard
            icon={Tag}
            title="Details"
            hint="Edited in the Edit dialog (name, slug, description) — not versioned."
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel className={subLabelClassName}>Name</FieldLabel>
                <FieldContent>
                  <Input value={templateName} disabled readOnly className="h-9" />
                </FieldContent>
              </Field>
              <Field>
                <FieldLabel className={subLabelClassName}>Description</FieldLabel>
                <FieldContent>
                  <Input value={templateDescription} disabled readOnly className="h-9" />
                </FieldContent>
              </Field>
            </div>
          </SectionCard>
        </div>
      </div>

      <div className="sticky bottom-4 mb-4 mt-2 rounded-lg border border-border/70 bg-background/95 backdrop-blur px-4 py-2.5 flex items-center justify-between gap-3">
        <span className="text-[12px] text-muted-foreground hidden sm:inline">
          Versions are immutable — submitting creates a new active version.
        </span>
        <div className="flex items-center gap-2 ml-auto">
          <Button
            type="button"
            variant="ghost"
            shape="pill"
            size="sm"
            onClick={onCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="tinted"
            shape="pill"
            size="sm"
            disabled={isPending}
            className="gap-1.5"
          >
            {isPending ? 'Creating…' : 'Create version'}
          </Button>
        </div>
      </div>
    </form>
  );
}
